(() => {
  if (customElements.get('krbobo-mobile-menu')) return;
  customElements.define('krbobo-mobile-menu', class extends HTMLElement {
    static get observedAttributes() { return ['open']; }
    get open() { return this.hasAttribute('open'); }
    set open(value) { this.toggleAttribute('open', Boolean(value)); }

    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const { signal } = this.events;
      this.triggers = [...document.querySelectorAll('[aria-controls="mobile-menu-drawer"]')].filter(button => !this.contains(button));
      this.header = this.closest('.shopify-section--header')?.querySelector('store-header') || this.triggers[0]?.closest('store-header');
      this.desktop = matchMedia('(min-width:1200px)');
      this.querySelectorAll('.kr-menu__dropdown').forEach(dropdown => {
        dropdown.hidden = false;
        const tabs = [...dropdown.querySelectorAll('[role="tab"]')].sort((a,b) => Number(a.dataset.categoryOrder) - Number(b.dataset.categoryOrder));
        const panels = [...dropdown.querySelectorAll('[role="tabpanel"]')];
        const select = index => {
          tabs.forEach((tab,i) => {
            const active = i === index;
            tab.setAttribute('aria-selected', String(active));
            tab.tabIndex = active ? 0 : -1;
            const panel = panels.find(panel => panel.id === tab.getAttribute('aria-controls'));
            panel.hidden = !active;
            panel.inert = !active;
            if (active) panel.querySelector('.kr-menu__products')?.scrollTo({ top:0 });
          });
        };
        tabs.forEach((tab,index) => {
          dropdown.querySelector('.kr-menu__categories').append(tab);
          tab.addEventListener('click', () => {
            select(index);
            tab.scrollIntoView({ block:'nearest', inline:'nearest' });
          }, { signal });
          tab.addEventListener('keydown', event => {
            let next;
            if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
            if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = tabs.length - 1;
            if (next === undefined) return;
            event.preventDefault();
            select(next);
            tabs[next].focus();
          }, { signal });
        });
        select(0);
      });
      document.addEventListener('keydown', event => {
        if (!this.open) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close(true);
        } else if (event.key === 'Tab') {
          const focusable = [...this.triggers, ...this.querySelectorAll('a[href],button:not([disabled]),[tabindex="0"]')].filter(el => el.getClientRects().length && !el.closest('[hidden],[inert]') && el.tabIndex >= 0);
          const index = focusable.indexOf(document.activeElement);
          if (!focusable.length) return;
          if (event.shiftKey && index <= 0) { event.preventDefault(); focusable.at(-1).focus(); }
          else if (!event.shiftKey && (index < 0 || index === focusable.length - 1)) { event.preventDefault(); focusable[0].focus(); }
        }
      }, { signal });
      document.addEventListener('pointerdown', event => {
        if (this.open && !this.contains(event.target) && !this.triggers.some(trigger => trigger.contains(event.target))) this.close();
      }, { signal });
      this.addEventListener('click', event => { if (event.target.closest('a[href]')) this.close(); }, { signal });
      window.addEventListener('resize', () => {
        if (this.desktop.matches) this.close();
        else if (this.open) this.position();
      }, { signal });
      window.visualViewport?.addEventListener('resize', () => { if (this.open) this.position(); }, { signal });
      window.visualViewport?.addEventListener('scroll', () => { if (this.open) this.position(); }, { signal });
      this.observer = new ResizeObserver(() => { if (this.open) this.position(); });
      if (this.header) this.observer.observe(this.header);
      this.syncOpen();
    }

    position() {
      const rect = this.header?.getBoundingClientRect();
      if (!rect) return;
      const viewport = window.visualViewport;
      this.style.setProperty('--mobile-menu-viewport-bottom', `${viewport ? viewport.offsetTop + viewport.height : innerHeight}px`);
      this.style.setProperty('--mobile-menu-top', `${Math.max(0,rect.bottom)}px`);
      this.style.setProperty('--mobile-menu-left', `${Math.max(0,rect.left)}px`);
      this.style.setProperty('--mobile-menu-width', `${Math.min(innerWidth,rect.width)}px`);
    }

    attributeChangedCallback() { if (this.events) this.syncOpen(); }

    syncOpen() {
      const open = this.open;
      // Capture the visible header before scroll locking changes sticky positioning.
      if (open && !document.documentElement.classList.contains('kr-mobile-menu-open') && this.header) {
        const rect = this.header.getBoundingClientRect();
        const top = 12;
        this.header.style.setProperty('--mobile-open-header-top', `${top}px`);
        this.header.style.setProperty('--mobile-open-header-left', `${rect.left}px`);
        this.header.style.setProperty('--mobile-open-header-width', `${rect.width}px`);
      }
      this.inert = !open;
      this.setAttribute('aria-hidden', String(!open));
      document.documentElement.classList.toggle('kr-mobile-menu-open', open);
      this.triggers?.forEach(trigger => trigger.setAttribute('aria-expanded', String(open)));
      if (open) { this.position(); this.scrollTop = 0; }
      this.dispatchEvent(new CustomEvent(open ? 'mobile-nav:open' : 'mobile-nav:close', { bubbles:true }));
      if (open) requestAnimationFrame(() => { if (this.open) this.position(); });
    }

    close(restoreFocus = false) {
      this.open = false;
      if (restoreFocus) this.triggers?.find(trigger => trigger.getClientRects().length)?.focus({ preventScroll:true });
    }

    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      document.documentElement.classList.remove('kr-mobile-menu-open');
      this.triggers?.forEach(trigger => {
        // Theme toggle-buttons dereference their target on true -> false.
        // During a section unload that target is already outside the document.
        trigger.removeAttribute('aria-expanded');
        trigger.setAttribute('aria-expanded','false');
      });
    }
  });
})();
